import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { ShoppingCart, Trash2, ArrowLeft, CreditCard, Shield, Lock, Clock } from 'lucide-react';

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, removeItem, clearCart, totalPrice, itemCount } = useCart();

  const handleCheckout = () => {
    const checkoutData = {
      source: 'coursedee',
      items: items,
      total: totalPrice,
      itemCount: itemCount,
      planName: items.map(item => item.name).join(' + '),
      planPrice: totalPrice,
      isCoursedeeCart: true,
      isNewPurchase: true
    };
    localStorage.setItem('coursedeeCartCheckout', JSON.stringify(checkoutData));
    navigate('/subscription/checkout-new?source=coursedee_cart');
  };

  if (itemCount === 0) {
    return (
      <div className="min-h-screen text-white relative overflow-hidden bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        {/* Background Image - Responsive */}
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat hidden md:block"
          style={{ backgroundImage: 'url(/coursedee-bg-web-checkout-new.jpg)' }}
        />
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat md:hidden"
          style={{ backgroundImage: 'url(/coursedee-bg-mobile-checkout-new.jpg)' }}
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/20"></div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <ShoppingCart className="w-24 h-24 mx-auto mb-6 text-purple-400/50" />
            <h1 className="text-4xl font-bold mb-4">ตระกร้าสินค้าว่างเปล่า</h1>
            <p className="text-gray-400 mb-8">คุณยังไม่ได้เพิ่มคอร์สใดๆ ลงในตระกร้า</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-purple-500/50"
            >
              <ArrowLeft size={20} />
              กลับไปเลือกคอร์ส
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative overflow-hidden bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Background Image - Responsive */}
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat hidden md:block"
        style={{ backgroundImage: 'url(/coursedee-bg-web-checkout-new.jpg)' }}
      />
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat md:hidden"
        style={{ backgroundImage: 'url(/coursedee-bg-mobile-checkout-new.jpg)' }}
      />
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20"></div>

      <div className="container mx-auto px-4 py-12 md:py-20 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
              <ShoppingCart className="text-purple-400" size={36} />
              ตะกร้าสินค้า
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (window.confirm('ล้างสินค้าทั้งหมดในตะกร้า?')) clearCart();
                }}
                className="flex items-center gap-1.5 text-red-400 hover:text-red-300 transition-colors text-sm md:text-base"
              >
                <Trash2 size={18} />
                <span>ล้างตะกร้า</span>
              </button>
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 text-sm md:text-base"
              >
                <ArrowLeft size={20} />
                <span className="hidden md:inline">กลับไปเลือกคอร์ส</span>
              </button>
            </div>
          </div>

          {/* Cart Items */}
          <div className="space-y-4 mb-6">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6 hover:border-purple-400/50 transition-all shadow-xl"
              >
                <div className="flex items-start gap-4">
                  {/* Icon/Badge */}
                  <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-2xl md:text-3xl font-bold">{item.version}</span>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-bold mb-1">{item.name}</h3>
                    <p className="text-gray-400 text-sm mb-2">{item.description}</p>
                    <p className="text-purple-400 font-semibold text-sm">{item.version}</p>
                  </div>
                  
                  {/* Price & Delete */}
                  <div className="flex flex-col items-end gap-3">
                    <p className="text-xl md:text-2xl font-bold text-purple-300">฿{item.price.toLocaleString()}</p>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-400 hover:text-red-300 transition-colors p-2 hover:bg-red-500/20 rounded-lg"
                      title="ลบออกจากตระกร้า"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 backdrop-blur-xl border border-purple-500/50 rounded-2xl p-6 md:p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-purple-500/30">
              <div>
                <p className="text-gray-400 text-sm mb-1">จำนวนคอร์ส</p>
                <p className="text-2xl font-bold">{itemCount} คอร์ส</p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm mb-1">ยอดรวมทั้งหมด</p>
                <p className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                  ฿{totalPrice.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-3 shadow-lg shadow-purple-500/50 mb-6"
            >
              <CreditCard size={24} />
              ดำเนินการชำระเงิน
            </button>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-purple-500/30">
              <div className="text-center">
                <Shield className="w-8 h-8 mx-auto mb-2 text-green-400" />
                <p className="text-xs text-gray-400">ชำระเงินปลอดภัย</p>
                <p className="text-xs font-semibold text-white">SSL Secured</p>
              </div>
              <div className="text-center">
                <Lock className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                <p className="text-xs text-gray-400">ข้อมูลของคุณปลอดภัย</p>
                <p className="text-xs font-semibold text-white">Data Protected</p>
              </div>
              <div className="text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                <p className="text-xs text-gray-400">พร้อมให้บริการ</p>
                <p className="text-xs font-semibold text-white">Support 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
